/**
 * The release gate's dialog (round 19's `ReleaseDialog`; docs/features/
 * order-to-invoice/57 §2.3): what the system checked, each with its evidence
 * and a link to the step it reads; the "date looks tight" warning, which
 * never blocks; what you're confirming and the one tick, "The file and the
 * brief match what was ordered". The button says what it does ("Release to
 * Solopress", "Release to production", "Release and hold"). *Hold…* keeps
 * the line at the gate with a reason.
 *
 * A bought line whose order has other unreleased lines for the same
 * supplier offers *Place now* or *Hold to place together* (doc 38 §2.7, D4);
 * *Place now*'s sub-label names the lines already held on its purchase
 * order, which go with it (Ben, 5 Oct, Q5). *Change supplier…* sits beside
 * the supplier in the description (Q6). A made line is released record-only
 * until step 7 (Q3).
 *
 * The line's print-ready files (the artwork family's, once its proof is
 * approved) sit with what you're confirming, each a download (doc 57 §2.1).
 *
 * Opened from the box's row or the Release step. With several lines it is
 * "Release all ready lines": one confirmation, a tick per line, each placed
 * with the others of its set. Every word is the view's.
 */
import { useId, useState } from "react";
import { Link } from "react-router";
import { Check, FileDown, TriangleAlert, X } from "lucide-react";
import type {
  LineReleaseView,
  OrderLineView,
  PrintReadyFile,
  ReleasePlacementName,
} from "@shared-core/data/order-to-invoice-types";
import type { OrderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";
import { FailureBanner, Field } from "./OrderDialogs";

export type ReleaseLines = (
  lines: Array<{ lineRef: string; isConfirmed: boolean }>,
  placement: ReleasePlacementName,
) => Promise<OrderStepFailure | null>;

/** ".02 Pull-up banner" */
function lineName(line: OrderLineView): string {
  return `${line.line_no} ${line.name.title}`;
}

/** "1,000", or the versions' split: "1,000: A Bristol 600 · B Leeds 400". */
function quantityOf(line: OrderLineView): string {
  const total = line.quantity.toLocaleString("en-GB");
  if (line.versions.length < 2) return total;
  return `${total}: ${line.versions
    .map((v) =>
      `${v.label ?? ""}${v.name ? ` ${v.name}` : ""} ${v.quantity.toLocaleString("en-GB")}`.trim(),
    )
    .join(" · ")}`;
}

function ChecksPanel({
  orderNo,
  line,
  release,
}: {
  orderNo: string;
  line: OrderLineView;
  release: LineReleaseView;
}) {
  return (
    <section aria-label="What the system checked" className="space-y-2">
      <h3 className="text-sm font-semibold">What the system checked</h3>
      <ul className="space-y-2 text-sm">
        {release.checks.map((check) => (
          <li key={check.id} className="flex gap-2">
            {check.is_passed ? (
              <Check
                aria-label="Passed"
                className="mt-0.5 size-4 shrink-0 text-emerald-600"
              />
            ) : (
              <X
                aria-label="Failed"
                className="mt-0.5 size-4 shrink-0 text-red-600"
              />
            )}
            <span className="min-w-0">
              <span className="font-medium">{check.label}</span>
              <span className="block text-xs text-muted-foreground">
                {check.link_step ? (
                  <Link
                    to={paths.orderDetail({
                      orderNo,
                      step: check.link_step,
                      line: line.line_no,
                    })}
                    className="underline-offset-2 hover:underline"
                  >
                    {check.evidence}
                  </Link>
                ) : (
                  check.evidence
                )}
              </span>
            </span>
          </li>
        ))}
        {release.date_warning && (
          <li className="flex gap-2">
            <TriangleAlert
              aria-label="Warning"
              className="mt-0.5 size-4 shrink-0 text-amber-600"
            />
            <span>
              <span className="font-medium">Date looks tight</span>
              <span className="block text-xs text-muted-foreground">
                {release.date_warning}. It doesn&apos;t block the release.
              </span>
            </span>
          </li>
        )}
        {release.delivery_warnings.map((warning) => (
          <li key={warning} className="flex gap-2">
            <TriangleAlert
              aria-label="Warning"
              className="mt-0.5 size-4 shrink-0 text-amber-600"
            />
            <span>
              <span className="font-medium">{warning}</span>
              <span className="block text-xs text-muted-foreground">
                It doesn&apos;t block the release.
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** One line: the checks, the confirmation, Release or Hold. */
function SingleRelease({
  orderNo,
  line,
  busy,
  onClose,
  onRelease,
  onHold,
  onChangeSupplier,
  onDownloadPrintReady,
}: {
  orderNo: string;
  line: OrderLineView;
  busy: boolean;
  onClose: () => void;
  onRelease: ReleaseLines;
  onHold: (reason: string) => Promise<OrderStepFailure | null>;
  onChangeSupplier?: () => void;
  onDownloadPrintReady?: (file: PrintReadyFile) => void;
}) {
  const id = useId();
  const r = line.release!;
  const [isConfirmed, setConfirmed] = useState(false);
  const [isHolding, setHolding] = useState(false);
  const [reason, setReason] = useState(r.hold?.reason ?? "");
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const [placement, setPlacement] = useState<ReleasePlacementName>("place_now");
  const choice = r.placement_choice;
  const canRelease = r.release_url !== null;
  const blocker = r.checks.find((c) => !c.is_passed)?.evidence ?? null;
  const reasonError =
    (touched && isHolding && !reason.trim()
      ? "Say why the order line is held."
      : undefined) ?? failure?.fieldErrors.reason;
  const confirmError =
    (touched && !isConfirmed && !isHolding
      ? "Confirm the file and the brief match what was ordered."
      : undefined) ?? failure?.fieldErrors["lines.0.is_confirmed"];
  return (
    <>
      <DialogHeader>
        <DialogTitle>Release {lineName(line)}</DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{r.description}</span>
            {onChangeSupplier && r.change_supplier && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={onChangeSupplier}
              >
                Change supplier…
              </Button>
            )}
          </div>
        </DialogDescription>
      </DialogHeader>
      <FailureBanner failure={failure} />
      {r.hold && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">
          {r.hold.label}
        </p>
      )}
      <div className="grid gap-5 md:grid-cols-2">
        <ChecksPanel orderNo={orderNo} line={line} release={r} />
        <section aria-label="What you're confirming" className="space-y-2">
          <h3 className="text-sm font-semibold">What you&apos;re confirming</h3>
          <dl className="space-y-1 text-sm">
            <div>
              <dt className="text-xs text-muted-foreground">What</dt>
              <dd>{line.name.full}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Quantity</dt>
              <dd className="tabular-nums">{quantityOf(line)}</dd>
            </div>
            {line.options.map((o) => (
              <div key={o.name}>
                <dt className="text-xs text-muted-foreground">{o.name}</dt>
                <dd>{o.value}</dd>
              </div>
            ))}
          </dl>
          {r.print_ready_files.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs text-muted-foreground">Print-ready</h4>
              <ul className="space-y-1">
                {r.print_ready_files.map((file) => (
                  <li key={file.download_url}>
                    <Button
                      type="button"
                      size="sm"
                      variant="link"
                      className="h-auto gap-1.5 px-0"
                      disabled={busy || !onDownloadPrintReady}
                      onClick={() => onDownloadPrintReady?.(file)}
                    >
                      <FileDown aria-hidden className="size-4" />
                      {[file.version_display, file.part_label]
                        .filter(Boolean)
                        .join(" · ")}
                      {file.version_display || file.part_label ? ": " : ""}
                      {file.file_name}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {canRelease && (
            <div className="space-y-1 pt-1">
              <div className="flex items-start gap-2">
                <Checkbox
                  id={`${id}-confirm`}
                  checked={isConfirmed}
                  onCheckedChange={(v) => setConfirmed(v === true)}
                  aria-invalid={confirmError ? true : undefined}
                  aria-describedby={
                    confirmError ? `${id}-confirm-error` : undefined
                  }
                />
                <Label htmlFor={`${id}-confirm`} className="font-normal">
                  The file and the brief match what was ordered
                </Label>
              </div>
              {confirmError && (
                <p id={`${id}-confirm-error`} className="text-sm text-red-600">
                  {confirmError}
                </p>
              )}
            </div>
          )}
        </section>
      </div>
      {canRelease && choice && r.to?.kind === "supplier" && (
        <section aria-label="When to place it" className="space-y-2">
          <h3 className="text-sm font-semibold">
            When to place it with {r.to.supplier_name}
          </h3>
          <RadioGroup
            value={placement}
            onValueChange={(v) => setPlacement(v as ReleasePlacementName)}
            className="gap-2"
          >
            <div className="flex items-start gap-2">
              <RadioGroupItem value="place_now" id={`${id}-now`} />
              <Label htmlFor={`${id}-now`} className="block font-normal">
                Place now
                <span className="block text-xs text-muted-foreground">
                  {choice.place_now_sentence}
                </span>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem
                value="hold_to_place_together"
                id={`${id}-together`}
              />
              <Label htmlFor={`${id}-together`} className="block font-normal">
                {choice.hold_label}
                <span className="block text-xs text-muted-foreground">
                  {choice.hold_sentence}
                </span>
              </Label>
            </div>
          </RadioGroup>
        </section>
      )}
      {!canRelease && !blocker && r.state === "ready" && (
        <p className="text-sm text-muted-foreground">
          This line releases itself. Hold it here to stop that.
        </p>
      )}
      {!canRelease && blocker && (
        <p className="text-sm text-muted-foreground">
          Can&apos;t release yet: {blocker}.
        </p>
      )}
      {isHolding && (
        <Field id={`${id}-reason`} label="Why hold it" error={reasonError}>
          {(aria) => (
            <Textarea
              {...aria}
              rows={2}
              value={reason}
              placeholder="Waiting for the customer to confirm the Leeds address"
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
      )}
      <DialogFooter className="gap-2 sm:justify-between">
        {r.hold_url ? (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={async () => {
              if (!isHolding) {
                setHolding(true);
                return;
              }
              setTouched(true);
              if (!reason.trim()) return;
              const refused = await onHold(reason.trim());
              setFailure(refused);
              if (!refused) onClose();
            }}
          >
            {isHolding ? "Hold at the gate" : r.hold ? "Change hold" : "Hold…"}
          </Button>
        ) : (
          <span />
        )}
        <span className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {r.release_label && (canRelease || r.state !== "ready") && (
            <Button
              type="button"
              disabled={busy || !canRelease || isHolding}
              onClick={async () => {
                setTouched(true);
                if (!isConfirmed) return;
                const refused = await onRelease(
                  [{ lineRef: line.line_ref, isConfirmed: true }],
                  choice ? placement : "place_now",
                );
                setFailure(refused);
                if (!refused) onClose();
              }}
            >
              {choice && placement === "hold_to_place_together"
                ? (r.release_and_hold_label ?? r.release_label)
                : r.release_label}
            </Button>
          )}
        </span>
      </DialogFooter>
    </>
  );
}

/** Several ready lines: one confirmation, a tick each. */
function ReleaseAll({
  lines,
  busy,
  onClose,
  onRelease,
}: {
  lines: OrderLineView[];
  busy: boolean;
  onClose: () => void;
  onRelease: ReleaseLines;
}) {
  const id = useId();
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  return (
    <>
      <DialogHeader>
        <DialogTitle>Release all ready lines</DialogTitle>
        <DialogDescription>
          Every check has passed on these. Tick each line whose file and brief
          match what was ordered.
        </DialogDescription>
      </DialogHeader>
      <FailureBanner failure={failure} />
      <ul className="divide-y divide-border text-sm">
        {lines.map((line) => {
          const r = line.release!;
          const target =
            r.to?.kind === "supplier"
              ? r.to.supplier_name
              : r.to?.kind === "stock"
                ? "stock"
                : "production";
          return (
            <li key={line.line_ref} className="flex items-start gap-3 py-2">
              <Checkbox
                id={`${id}-${line.line_no}`}
                checked={ticked.has(line.line_ref)}
                onCheckedChange={(v) =>
                  setTicked((t) => {
                    const next = new Set(t);
                    if (v === true) next.add(line.line_ref);
                    else next.delete(line.line_ref);
                    return next;
                  })
                }
              />
              <Label
                htmlFor={`${id}-${line.line_no}`}
                className="block font-normal"
              >
                <span className="font-medium">{lineName(line)}</span>
                <span className="text-muted-foreground">
                  {" "}
                  · {quantityOf(line)} · to {target}
                </span>
                <span className="block text-xs text-muted-foreground">
                  The file and the brief match what was ordered
                  {r.date_warning && ` · date looks tight: ${r.date_warning}`}
                </span>
              </Label>
            </li>
          );
        })}
      </ul>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={busy || ticked.size === 0}
          onClick={async () => {
            // Released together, each bought line is placed with the others
            // of its set: nothing is held.
            const refused = await onRelease(
              lines
                .filter((l) => ticked.has(l.line_ref))
                .map((l) => ({ lineRef: l.line_ref, isConfirmed: true })),
              "place_now",
            );
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          Release {ticked.size === 1 ? "1 line" : `${ticked.size} lines`}
        </Button>
      </DialogFooter>
    </>
  );
}

export function ReleaseDialog({
  orderNo,
  lines,
  busy,
  onClose,
  onRelease,
  onHold,
  onChangeSupplier,
  onDownloadPrintReady,
}: {
  orderNo: string;
  /** One line, or the ready lines for "Release all ready lines". */
  lines: OrderLineView[];
  busy: boolean;
  onClose: () => void;
  onRelease: ReleaseLines;
  onHold: (lineRef: string, reason: string) => Promise<OrderStepFailure | null>;
  /** *Change supplier…* for the one line (Q6), when its release offers it. */
  onChangeSupplier?: (lineRef: string) => void;
  /** Download one of the line's print-ready files (the read's `download_url`). */
  onDownloadPrintReady?: (file: PrintReadyFile) => void;
}) {
  const single = lines.length === 1 ? lines[0]! : null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        {single ? (
          <SingleRelease
            orderNo={orderNo}
            line={single}
            busy={busy}
            onClose={onClose}
            onRelease={onRelease}
            onHold={(reason) => onHold(single.line_ref, reason)}
            onChangeSupplier={
              onChangeSupplier
                ? () => onChangeSupplier(single.line_ref)
                : undefined
            }
            onDownloadPrintReady={onDownloadPrintReady}
          />
        ) : (
          <ReleaseAll
            lines={lines}
            busy={busy}
            onClose={onClose}
            onRelease={onRelease}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
